import { useEffect, useRef, useState, useCallback } from 'react'
import type { ChatMessage } from './useClaudeSession'
import { extractSpeakableText } from '../lib/speech'
import { isSpeechActive } from '../lib/speechPlayer'

interface CommentarySpeech {
  speaking: boolean
  loading: boolean
  speak: (text: string) => void
}

type AssistantShape = {
  message?: {
    id?: string
    stop_reason?: string | null
    content?: Parameters<typeof extractSpeakableText>[0]
  }
}

/** Streaming spoken commentary for hands-free voice mode.
 *
 *  A working turn isn't one reply — it's an intro, short notes between tool
 *  calls, then a final summary, each its own top-level assistant message.
 *  Waiting for the turn to finish before speaking means listening to minutes
 *  of progress ticks with no idea what's happening. This hook reads each
 *  assistant message aloud AS IT COMPLETES, so the narration tracks the work.
 *
 *  Completion matters because messages stream: with partial messages enabled
 *  the same message id arrives repeatedly with `stop_reason: null` and merges
 *  in place until the final version (stop_reason set) lands. Only that final
 *  version is queued — keyed by message id, so later merges never re-read it.
 *  When the turn ends (`isWorking` drops), any message still unspoken is swept
 *  into the queue regardless of stop_reason (e.g. a turn cut by interrupt).
 *
 *  Completion is NOT read from `stop_reason` alone. An agent only starts a new
 *  message after closing the previous one, so anything with a later message
 *  after it is finished by construction — which keeps narration streaming even
 *  when an agent doesn't populate the field. (Codex didn't; every message
 *  looked in-flight, so nothing qualified until the end-of-turn sweep and the
 *  entire turn was read out in one go at the end.) Only the LAST message needs
 *  its stop_reason consulted, because it's the only one that can still grow.
 *
 *  Queued texts play through the caller's speech handle, and the pump drains
 *  the whole queue into ONE utterance rather than speaking items one at a
 *  time. Each speak() starts a fresh synthesis pipeline, so per-item playback
 *  put a silent time-to-first-audio gap between every note; the common case
 *  (one item queued) is unchanged, while a backlog now reads as continuous
 *  narration. The pump re-checks `isSpeechActive()` — the synchronous module
 *  registry — because the React `speaking` flag lags a render behind speak(),
 *  and two pump runs in that gap would cut the first reading short.
 *
 *  Turning voice mode on marks existing history as spoken, so narration covers
 *  what happens after the toggle rather than the backlog — with one deliberate
 *  exception: a message being written AT THAT MOMENT is left unspoken. Enabling
 *  voice mid-turn is a request to hear what the agent is doing now, and the
 *  sentence already in flight is the nearest answer to that.
 */
export function useVoiceCommentary(opts: {
  enabled: boolean
  /** Top-level chat messages only — subagent children are never narrated. */
  messages: ChatMessage[]
  isWorking: boolean
  speech: CommentarySpeech
}) {
  const { enabled, messages, isWorking, speech } = opts
  const queueRef = useRef<string[]>([])
  const spokenKeysRef = useRef<Set<string>>(new Set())
  const [queueTick, setQueueTick] = useState(0)

  // Watcher: seed on the enable rising edge, then queue every newly completed
  // assistant message. Runs before the pump below (declaration order) so a
  // message queued in this render can start speaking in the same pass.
  const prevEnabledRef = useRef(enabled)
  useEffect(() => {
    const was = prevEnabledRef.current
    prevEnabledRef.current = enabled
    if (!enabled) {
      queueRef.current = []
      return
    }
    const spoken = spokenKeysRef.current
    if (!was) {
      spoken.clear()
      for (let i = 0; i < messages.length; i++) {
        const m = messages[i]
        if (m.type !== 'assistant') continue
        // Everything already finished counts as heard. The one exception is a
        // message the agent is writing right now: that's what the user turned
        // voice on to hear, so leave it unspoken and let it queue when it
        // completes. Gated on isWorking so an idle panel never re-reads the
        // last reply just because it lacks a stop_reason.
        if (isWorking && isInFlight(messages, i)) continue
        spoken.add(keyOf(m as AssistantShape, i))
      }
      return
    }
    let queued = false
    for (let i = 0; i < messages.length; i++) {
      const m = messages[i]
      if (m.type !== 'assistant') continue
      const key = keyOf(m as AssistantShape, i)
      if (spoken.has(key)) continue
      // Still being written — wait for the final version. Everything else is
      // finished by construction (see isInFlight).
      if (isWorking && isInFlight(messages, i)) continue
      spoken.add(key)
      const text = extractSpeakableText((m as AssistantShape).message?.content || [])
      if (text) {
        queueRef.current.push(text)
        queued = true
      }
    }
    if (queued) setQueueTick(t => t + 1)
  }, [enabled, messages, isWorking])

  // Pump: play the next queued text once nothing is speaking. Deliberately
  // re-runs on every render (the speech handle is a fresh object each time);
  // the guards make that free, and it means the queue drains no matter which
  // state change ended the previous playback.
  useEffect(() => {
    if (!enabled) return
    if (speech.speaking || speech.loading || isSpeechActive()) return
    if (!queueRef.current.length) return
    // Drain the whole queue into one reading. Speaking items individually
    // costs a fresh synthesis round-trip — and an audible silence — at every
    // boundary; a single utterance has none. With one item queued (the normal
    // streaming case) this is exactly the old behaviour.
    const batch = queueRef.current.splice(0, queueRef.current.length)
    speech.speak(batch.join('\n\n'))
  }, [enabled, queueTick, speech])

  /** Drop everything not yet spoken (skip button, voice mode off). The
   *  currently playing text is the caller's to stop; new completions after
   *  this still queue and speak. */
  const clear = useCallback(() => {
    queueRef.current = []
  }, [])

  return { clear }
}

function keyOf(m: AssistantShape, index: number): string {
  return m.message?.id ?? `noid-${index}`
}

/**
 * Is the message at `index` still being written?
 *
 * Only the last message can be — an agent closes one message before opening
 * the next, so anything followed by another message is finished whatever its
 * `stop_reason` says. That structural check is what keeps narration streaming
 * for agents that never populate the field, and it means the flag is consulted
 * for exactly one message: the tail.
 */
function isInFlight(messages: { type?: string }[], index: number): boolean {
  if (index !== messages.length - 1) return false
  return (messages[index] as AssistantShape).message?.stop_reason == null
}
